import React from 'react'
import {
  Button,
  Select,
  Spin,
  Input,
  Dropdown,
} from '@surecompanies/core_components'
import { DownOutlined } from '@ant-design/icons'
import { NOT_AVAILABLE } from '../Utils/types'
import type { VcnPaymentManagementProps } from './VcnPaymentManagement.types'
import {
  PaymentMethodContainer,
  PaymentMethodHeader,
  Divider,
  EditHeader,
  MethodDisplayRow,
  MethodLabel,
  MethodValue,
  EditFormRow,
  FormField,
  FormLabel,
  ActionButtonsRow,
  GenerateVcnLink,
  SubmitButtonsRow,
} from './VcnPaymentManagement.styles'
import { VcnDetailsTable } from './VcnDetailsTable'
import { AchDetailsTable } from './AchDetailsTable'
import { VcnActionsSelect } from './VcnActionsSelect'
import { VcnCardSelect } from './VcnCardSelect'
import { GenerateVcnModal } from './GenerateVcnModal'
import { MarqetaConfirmModal } from './MarqetaConfirmModal'
import { usePaymentManagement } from './hooks/usePaymentManagement'

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components for cleaner organization
// ─────────────────────────────────────────────────────────────────────────────

interface ModalsProps {
  generateModalOpen: boolean
  closeGenerateModal: () => void
  handleGenerateVcnConfirm: (reason: string, provider: any) => void
  handleGenerateVcnWithActivityLog: (
    provider: any,
    options: {
      reason?: string
      reasonCategory?: string
      zendeskTicket?: string
    }
  ) => void
  isGenerating: boolean
  isTerminatingAll: boolean
  providerAvailability: { enabledProviders: any[] }
  shouldShowTerminateWarning: boolean
  selectedVcnProvider: any
  enableNoteModalBeforeCreate: boolean
  personFullName: string
  showMarqetaConfirm: boolean
  handleMarqetaCancel: () => void
  handleMarqetaConfirm: () => void
}

function PaymentModals({
  generateModalOpen,
  closeGenerateModal,
  handleGenerateVcnConfirm,
  handleGenerateVcnWithActivityLog,
  isGenerating,
  isTerminatingAll,
  providerAvailability,
  shouldShowTerminateWarning,
  selectedVcnProvider,
  enableNoteModalBeforeCreate,
  personFullName,
  showMarqetaConfirm,
  handleMarqetaCancel,
  handleMarqetaConfirm,
}: ModalsProps) {
  return (
    <>
      <GenerateVcnModal
        open={generateModalOpen}
        onCancel={closeGenerateModal}
        onConfirm={handleGenerateVcnConfirm}
        onConfirmWithActivityLog={handleGenerateVcnWithActivityLog}
        loading={isGenerating || isTerminatingAll}
        enabledProviders={providerAvailability.enabledProviders}
        showTerminateWarning={shouldShowTerminateWarning}
        selectedProvider={selectedVcnProvider}
        useActivityLogPopup={enableNoteModalBeforeCreate}
        personFullName={personFullName}
      />

      <MarqetaConfirmModal
        open={showMarqetaConfirm}
        onCancel={handleMarqetaCancel}
        onConfirm={handleMarqetaConfirm}
      />
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function PaymentManagementSection({
  paymentCase,
  onCopy,
  canUpdateCase = false,
  onComplete,
}: VcnPaymentManagementProps) {
  const pm = usePaymentManagement({
    paymentCase,
    canUpdateCase,
    onComplete,
  })

  // ─────────────────────────────────────────────────────────────────────────────
  // Simple view mode for non-VCN/ACH/CHECK/GROUP_ACH methods
  // ─────────────────────────────────────────────────────────────────────────────
  if (
    !pm.isEditing &&
    !pm.isVcnMethod &&
    !pm.isAchMethod &&
    !pm.isCheckMethod &&
    !pm.isGroupAchMethod
  ) {
    return (
      <PaymentMethodContainer>
        <PaymentMethodHeader>Primary Payment Method</PaymentMethodHeader>

        <MethodDisplayRow>
          <MethodLabel>Method:</MethodLabel>
          <MethodValue>{pm.primaryPaymentMethod ?? NOT_AVAILABLE}</MethodValue>
          {canUpdateCase && (
            <Button type="default" size="small" onClick={pm.handleEditClick}>
              Edit
            </Button>
          )}
        </MethodDisplayRow>

        <PaymentModals
          generateModalOpen={pm.generateModalOpen}
          closeGenerateModal={pm.closeGenerateModal}
          handleGenerateVcnConfirm={pm.handleGenerateVcnConfirm}
          handleGenerateVcnWithActivityLog={pm.handleGenerateVcnWithActivityLog}
          isGenerating={pm.isGenerating}
          isTerminatingAll={pm.isTerminatingAll}
          providerAvailability={pm.providerAvailability}
          shouldShowTerminateWarning={pm.shouldShowTerminateWarning}
          selectedVcnProvider={pm.selectedVcnProvider}
          enableNoteModalBeforeCreate={pm.enableNoteModalBeforeCreate}
          personFullName={pm.personFullName}
          showMarqetaConfirm={pm.showMarqetaConfirm}
          handleMarqetaCancel={pm.handleMarqetaCancel}
          handleMarqetaConfirm={pm.handleMarqetaConfirm}
        />
      </PaymentMethodContainer>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Full edit-style layout for VCN, ACH, CHECK, or GROUP_ACH
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <PaymentMethodContainer>
      <PaymentMethodHeader>Primary Payment Method</PaymentMethodHeader>
      <Divider />
      <EditHeader>Edit payment method</EditHeader>

      <EditFormRow>
        {/* Primary Method Select */}
        <FormField>
          <FormLabel>Select primary method</FormLabel>
          <Select
            placeholder="Select"
            value={pm.displayMethod}
            onChange={pm.handleMethodChange}
            onDropdownVisibleChange={pm.handleMethodDropdownOpen}
            options={pm.primaryPaymentMethodOptions}
            style={{ width: '100%' }}
            disabled={!canUpdateCase || pm.isAnyLoading}
          />
        </FormField>

        {/* ACH Account Number - only show when editing */}
        {pm.isAchSelected && pm.isEditing && (
          <FormField>
            <FormLabel>ACH account number</FormLabel>
            <Input
              placeholder="e.g. 1234567890"
              value={pm.achAccountNumberInput}
              onChange={(e) => pm.setAchAccountNumberInput(e.target.value)}
              disabled={!canUpdateCase || pm.isAnyLoading}
            />
          </FormField>
        )}

        {/* VCN Selection and Actions - only show when NOT in edit mode */}
        {pm.isVcnSelected && !pm.isEditing && (
          <>
            <FormField>
              <FormLabel>Select VCN</FormLabel>
              <VcnCardSelect
                value={pm.selectedVcnCard}
                onChange={pm.setSelectedVcnCard}
                availableCards={pm.availableCards}
                virtualCardKey={pm.virtualCardKey}
                currentHealthPlanId={pm.currentHealthPlanId}
                loading={pm.isLoadingAvailableCards}
                disabled={!canUpdateCase || pm.isAnyLoading}
              />
            </FormField>

            <ActionButtonsRow>
              <Button
                type="default"
                onClick={pm.handleReuseVcn}
                disabled={
                  !canUpdateCase ||
                  pm.selectedVcnCard == null ||
                  pm.isAnyLoading ||
                  pm.isLoadingAvailableCards
                }
                loading={pm.isReusing}
              >
                Reuse VCN
              </Button>
              <Dropdown
                menu={{
                  items: pm.generateVcnMenuItems,
                  onClick: pm.handleGenerateVcnMenuClick,
                }}
                trigger={['click']}
                disabled={
                  !canUpdateCase ||
                  !pm.providerAvailability.hasAnyProvider ||
                  pm.isAnyLoading ||
                  pm.isGenerateDisabledForTerminateAll
                }
              >
                <GenerateVcnLink
                  type="button"
                  disabled={
                    !canUpdateCase ||
                    !pm.providerAvailability.hasAnyProvider ||
                    pm.isAnyLoading ||
                    pm.isGenerateDisabledForTerminateAll
                  }
                >
                  Generate VCN <DownOutlined style={{ fontSize: 10 }} />
                </GenerateVcnLink>
              </Dropdown>
            </ActionButtonsRow>
          </>
        )}
      </EditFormRow>

      {/* Loading state */}
      {pm.isVcnSelected && pm.virtualCardLoading && (
        <div style={{ padding: '16px 0' }}>
          <Spin size="small" />
        </div>
      )}

      {/* Error states */}
      {pm.isVcnSelected && pm.wexMissingCardId && (
        <div style={{ color: '#ff4d4f', marginTop: 16 }}>
          WEX card requires a virtual card ID on the benefit; it was not
          included in this assignment detail response.
        </div>
      )}

      {pm.isVcnSelected && pm.virtualCardError && (
        <div style={{ color: '#ff4d4f', marginTop: 16 }}>
          Could not load virtual card details.
        </div>
      )}

      {/* VCN Details Table */}
      {pm.isVcnSelected && pm.showVcnDetails && (
        <VcnDetailsTable virtualCard={pm.virtualCard!} onCopy={onCopy} />
      )}

      {/* ACH Details Table or Edit Form */}
      {pm.isAchSelected && !pm.isEditing && (
        <>
          <AchDetailsTable
            fullName={pm.personFullName}
            accountNumber={pm.achAccountNumber}
            mailingAddress={pm.mailingAddress}
            accountType={pm.achAccountType}
            accountUse={pm.achAccountUse}
            onCopy={onCopy}
          />
          {canUpdateCase && (
            <SubmitButtonsRow>
              <Button type="primary" onClick={pm.handleEditClick}>
                Edit
              </Button>
            </SubmitButtonsRow>
          )}
        </>
      )}

      {/* VCN Actions dropdown - when not editing and has active card */}
      {pm.isVcnSelected &&
        !pm.isEditing &&
        pm.showVcnDetails &&
        canUpdateCase && (
          <>
            <SubmitButtonsRow>
              <VcnActionsSelect
                cardState={pm.virtualCard?.state ?? ''}
                value={pm.selectedAction}
                onActionSelect={pm.handleActionSelect}
                disabled={pm.isAnyLoading}
                loading={pm.isPerformingAction || pm.isRefetching}
              />
              {pm.selectedAction === 'generate' && (
                <FormField>
                  <FormLabel>VCN Provider</FormLabel>
                  <Select
                    placeholder="Select provider"
                    value={pm.selectedActionProvider}
                    onChange={pm.setSelectedActionProvider}
                    options={pm.providerSelectOptions}
                    style={{ width: '100%' }}
                    disabled={pm.isAnyLoading}
                  />
                </FormField>
              )}
            </SubmitButtonsRow>
            <SubmitButtonsRow>
              <Button
                type="primary"
                onClick={pm.handleActionSubmit}
                loading={
                  pm.isPerformingAction || pm.isGenerating || pm.isRefetching
                }
                disabled={
                  !pm.selectedAction ||
                  (pm.selectedAction === 'generate' &&
                    !pm.selectedActionProvider) ||
                  pm.isAnyLoading
                }
              >
                Submit
              </Button>
              {pm.selectedAction && (
                <Button
                  type="default"
                  onClick={pm.handleActionCancel}
                  disabled={
                    pm.isPerformingAction || pm.isGenerating || pm.isRefetching
                  }
                >
                  Cancel
                </Button>
              )}
            </SubmitButtonsRow>
          </>
        )}

      {/* Submit/Cancel buttons - only show when in edit mode */}
      {pm.isEditing && canUpdateCase && (
        <SubmitButtonsRow>
          <Button
            type="primary"
            onClick={pm.handleSubmit}
            loading={pm.isSubmitting || pm.isRefetching}
            disabled={
              !pm.displayMethod ||
              pm.isAnyLoading ||
              (pm.isAchSelected && !pm.achAccountNumberInput.trim())
            }
          >
            Submit
          </Button>
          <Button
            type="default"
            onClick={pm.handleCancelEdit}
            disabled={pm.isAnyLoading}
          >
            Cancel
          </Button>
        </SubmitButtonsRow>
      )}

      {/* Modals */}
      <PaymentModals
        generateModalOpen={pm.generateModalOpen}
        closeGenerateModal={pm.closeGenerateModal}
        handleGenerateVcnConfirm={pm.handleGenerateVcnConfirm}
        handleGenerateVcnWithActivityLog={pm.handleGenerateVcnWithActivityLog}
        isGenerating={pm.isGenerating}
        isTerminatingAll={pm.isTerminatingAll}
        providerAvailability={pm.providerAvailability}
        shouldShowTerminateWarning={pm.shouldShowTerminateWarning}
        selectedVcnProvider={pm.selectedVcnProvider}
        enableNoteModalBeforeCreate={pm.enableNoteModalBeforeCreate}
        personFullName={pm.personFullName}
        showMarqetaConfirm={pm.showMarqetaConfirm}
        handleMarqetaCancel={pm.handleMarqetaCancel}
        handleMarqetaConfirm={pm.handleMarqetaConfirm}
      />
    </PaymentMethodContainer>
  )
}
